import styled, { keyframes } from 'styled-components';

interface IContainerProps {
  width?: string;
  height?: string;
}

const copySuccessFeedback = keyframes`
  0% {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(76, 175, 80, 0.5);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  }
  50% {
    background: rgba(232, 245, 233, 0.98);
    border-color: rgba(76, 175, 80, 0.7);
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.2);
  }
  100% {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const Container = styled.div<IContainerProps>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '300px'};
  border-radius: 6px;
`;

export const MarkerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transform: translate(-50%, -100%);
  position: relative;

  &:hover {
    z-index: 1000;
  }
`;

interface ICoordinateLabelProps {
  $copied?: boolean;
}

export const CoordinateLabel = styled.div<ICoordinateLabelProps>`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid ${({ $copied }) => ($copied ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.1)')};
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  font-size: 11px;
  font-family: Montserrat, sans-serif;
  white-space: nowrap;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 180px;
  animation: ${({ $copied }) => ($copied ? copySuccessFeedback : 'none')} 0.6s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
    border-color: rgba(25, 118, 210, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

export const LabelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

export const LabelTitle = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #999;
`;

export const ClusterBadge = styled.div`
  font-family: Montserrat, sans-serif;
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(25, 118, 210, 0.3);
`;

export const CoordinateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

export const CoordinateKey = styled.span`
  font-family: Montserrat, sans-serif;
  color: #666;
  font-weight: 700;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  min-width: 30px;
`;

export const CoordinateValue = styled.span`
  color: #1976d2;
  font-weight: 600;
  font-size: 12px;
  flex: 1;
  text-align: right;
  font-family: Montserrat, sans-serif;
`;

interface ICopyHintProps {
  $copied?: boolean;
}

export const CopyHint = styled.div<ICopyHintProps>`
  font-family: Montserrat, sans-serif;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 8px;
  color: ${({ $copied }) => ($copied ? '#2e7d32' : '#999')};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${({ $copied }) => ($copied ? '700' : '600')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: color 0.3s ease;
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

export const IconWrapper = styled.div<{ $hasCluster?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: filter 0.2s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));

  &:hover {
    filter: drop-shadow(0 4px 8px rgba(25, 118, 210, 0.4));
    animation: ${({ $hasCluster }) => ($hasCluster ? pulseAnimation : 'none')} 0.8s ease infinite;
  }
`;

export const ClusterCountBadge = styled.div`
  font-family: Montserrat, sans-serif;
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 6px;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(238, 90, 111, 0.5);
  min-width: 20px;
  text-align: center;
  border: 2px solid white;
`;
